
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { plainToInstance } from "class-transformer";

import subscription_routes from "@/api/routes/subscriptions";

import Preloader from "@/components/Preloader.vue";
import CFileDownload from "@/components/FileDownload.vue";

import SubscriptionProgram from "@/models/subscriptions/program";
import { ESubscriptionStatus } from "@/models/subscriptions/status";
import SubscriptionUnit from "@/models/subscriptions/unit";

import CSubscriptionUnitScheduleStatus from "./unit/ScheduleStatus.vue";
import { EUnitableType } from "@/models/unit/unit_version";
import DocumentVersion from "@/models/unit/unitable/document/version";
import PracticalVersion from "@/models/unit/unitable/practical/version";

import CSubscriptionUnitDocument from "./unit/Document.vue";
import CSubscriptionUnitTest from "./unit/Test.vue";
import CSubscriptionUnitPractical from "./unit/Practical.vue";
import CSubscriptionUnitEvent from "./unit/Event.vue";
import CSubscriptionUnitLearningTime from "./unit/SubscriptionUnitLearningTime.vue";

@Component({
  name: "VSubscriptionUnit",
  components: {
    Preloader,
    CSubscriptionUnitScheduleStatus,
    CFileDownload,
    CSubscriptionUnitDocument,
    CSubscriptionUnitTest,
    CSubscriptionUnitPractical,
    CSubscriptionUnitEvent,
    CSubscriptionUnitLearningTime
  }
})
export default class VSubscriptionUnit extends Vue {
  @Prop({ required: true }) public id!: string;
  @Prop({ required: true }) public unit_id!: string;

  public preload: boolean = false;

  private current_user: IJWTUser | null = this.$store.state.currentUser;
  private watchers: Function[] = [];

  public is_current_user_subscription: boolean = false;
  private next_unit_id: string | null = null;

  public subscription_program: SubscriptionProgram | null = null;
  public subscription_unit: SubscriptionUnit | null = null;

  protected beforeDestroy() {
    this.watchers.forEach(unwatch => {
      unwatch();
    });
  }

  protected async created() {
    this.watchers.push(
      this.$store.watch(
        state => state.currentUser,
        currentUser => {
          this.current_user = currentUser;

          this.setIsCurrentUserSubscription();
        }
      )
    );

    await this.loadSubscriptions();
  }

  @Watch("unit_id")
  protected async changeUnitId() {
    this.preload = true;

    return this.loadSubscriptionUnit()
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          speed: 500
        });
      })
      .finally(() => {
        this.preload = false;
      });
  }

  public isDocument() {
    if (this.subscription_unit) {
      return (
        this.subscription_unit.unit_version.unitable_type ===
        EUnitableType["Document::Version"]
      );
    }

    return false;
  }

  public isTest() {
    if (this.subscription_unit) {
      return (
        this.subscription_unit.unit_version.unitable_type ===
        EUnitableType["Test::Version"]
      );
    }

    return false;
  }

  public isPractical() {
    if (this.subscription_unit) {
      return (
        this.subscription_unit.unit_version.unitable_type ===
        EUnitableType["Practical::Version"]
      );
    }

    return false;
  }

  public isEvent() {
    if (this.subscription_unit) {
      return (
        this.subscription_unit.unit_version.unitable_type ===
        EUnitableType["Event::Version"]
      );
    }

    return false;
  }

  public isNeedSaveLearningTime() {
    if (this.subscription_unit && this.is_current_user_subscription) {
      return [
        ESubscriptionStatus.NOT_STARTED,
        ESubscriptionStatus.IN_PROGRESS
      ].includes(this.subscription_unit.status);
    }

    return false;
  }

  public getUnitableUrl() {
    if (this.subscription_unit) {
      switch (this.subscription_unit.unit_version.unitable_type) {
        case EUnitableType["Document::Version"]:
          return (this.subscription_unit.unit_version
            .unitable as DocumentVersion).url;
        case EUnitableType["Practical::Version"]:
          return (this.subscription_unit.unit_version
            .unitable as PracticalVersion).url;
        default:
          return null;
      }
    }

    return null;
  }

  private async loadSubscriptions() {
    this.preload = true;

    await Promise.all([
      this.loadSubscriptionProgram(),
      this.loadSubscriptionUnit()
    ])
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          speed: 500
        });
      })
      .finally(() => {
        this.preload = false;
      });
  }

  private setIsCurrentUserSubscription() {
    if (this.subscription_program && this.current_user) {
      this.is_current_user_subscription =
        this.subscription_program.user_id === this.current_user.id;
    } else {
      this.is_current_user_subscription = false;
    }
  }

  private async loadSubscriptionProgram() {
    return this.$api
      .get(subscription_routes.subscription_program(this.id), {
        params: {
          units: false,
          inspectors: false
        }
      })
      .then(({ data: res }: { data: SubscriptionProgram }) => {
        this.subscription_program = plainToInstance(SubscriptionProgram, res);

        this.setIsCurrentUserSubscription();
      });
  }

  private async loadSubscriptionUnit() {
    return this.$api
      .get(subscription_routes.subscription_unit(this.unit_id), {
        params: {
          units: true,
          results: true
        }
      })
      .then(({ data: res }: { data: SubscriptionUnit }) => {
        this.subscription_unit = plainToInstance(SubscriptionUnit, res);
      })
      .then(async () => {
        await this.loadNextSubscriptionUnitId();
      });
  }

  private async loadNextSubscriptionUnitId() {
    if (this.subscription_unit) {
      return this.$api
        .get(subscription_routes.not_started_unit(this.id), {
          params: {
            unit_id: this.unit_id,
            level: this.subscription_unit.relation_version_level
          }
        })
        .then(({ data: res }: { data: string }) => {
          this.next_unit_id = res;
        });
    }
  }

  public async reassign() {
    return this.$api
      .get(subscription_routes.reassign(this.unit_id))
      .then(({ data: res }: { data: SubscriptionUnit }) => {
        this.$router.push({
          name: "subscription_unit",
          params: {
            id: this.id,
            unit_id: res.id
          }
        });
      });
  }
}
